import { Card, CardContent, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import { TooltipProps } from 'recharts/types/component/Tooltip';
import { Rectangle } from '../../../../../common/components/Rectangle';
import { TypographyOverflow } from '../../../../../common/components/TypographyOverflow';
import { Severity, getLabel } from '../../../../../common/enums/Severity';

type Props = TooltipProps<number, string> & {
  includePassed?: boolean;
};

export const ChartTooltip = (props: Props = {}) => {
  const { active, payload, includePassed } = props;
  if (!active || !payload || !payload.length) {
    return null;
  }

  // We have at least one payload and internally it contains previously declared value
  const label = payload[0].payload.rangeName as string;

  const payloadData = payload.filter((barData) => includePassed || barData.dataKey !== 'passed');

  const total = payloadData.reduce((sum, barData) => sum + (barData.value || 0), 0);
  const showWiderCard = total >= 100_000;
  return (
    <Card>
      <CardContent sx={{ pt: 1, px: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="body2" sx={{ fontWeight: 500, px: 0.75, mb: 1 }}>
          {label}
        </Typography>
        <TableContainer sx={{ height: '100%' }}>
          <Table
            data-role="chart-legend-container"
            role="presentation"
            size="small"
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              maxWidth: showWiderCard ? '250px' : '220px',
              tbody: {
                th: {
                  width: showWiderCard ? '50%' : '60%',
                },
                'th, td': {
                  border: 0,
                  px: 0.5,
                  py: 0.25,
                },
              },
            }}
          >
            <TableBody>
              {payloadData.map((barData) => {
                const dataKey = barData.dataKey as Severity;
                const color = barData.color || '';
                const value = barData.value || 0;
                let percentage = 0;
                if (total > 0) {
                  percentage = (value / total) * 100;
                }
                return (
                  <TableRow key={dataKey}>
                    <TableCell component="th" scope="row">
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Rectangle color={color} width="8px" height="8px" />
                        <TypographyOverflow variant="body2">{getLabel(dataKey)}</TypographyOverflow>
                      </div>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {percentage.format({ decimals: 0 })}%
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                        {value}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};
